import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabNav, Pill } from '../';
import ExteletBuildSurface from './ExteletBuildSurface';
import ExteletDecideTab from './ExteletDecideTab';
import ExteletActTab from './ExteletActTab';

/**
 * ExteletPromote — Prove7 journey Step 5 ("Promote", formerly "Trust Gate").
 *
 * Hosts the ExteLet Build · Decide · Act promotion gate as an inner sub-nav.
 * Pulled out of AgentDetailPageV2 in the Prove7-journey redesign (J1b): the
 * B-D-A phases are no longer top-level tabs — they live under this single
 * "Promote" step, while Identity / Intent / RBAC graduated to their own steps.
 *
 * The three surfaces (ExteletBuildSurface / ExteletDecideTab / ExteletActTab)
 * are rendered with EXACTLY the same props they had as top-level tabs — only
 * their parent moved. The inner phase dot mirrors agent.phase progression
 * (cleared = success, current = action, future = none), matching the native
 * phase-chevron semantics.
 *
 * @param {Object}   props
 * @param {Object}   props.agent
 * @param {Object}   props.registration
 * @param {Function} props.reloadRegistration
 * @param {Function} props.reloadAgent
 * @param {boolean}  props.discoveryRunning
 * @param {Array}    props.discoveredSkills
 * @param {Function} props.setDiscoveredSkills
 * @param {Function} props.handleDiscoverInline
 * @param {Function} props.onAdvancePhase
 */
export default function ExteletPromote({
  agent,
  registration,
  reloadRegistration,
  reloadAgent,
  discoveryRunning,
  discoveredSkills,
  setDiscoveredSkills,
  handleDiscoverInline,
  onAdvancePhase,
}) {
  // ExteLets: external_agent_registration.registrationStatus is the AUTHORITATIVE
  // phase (the GEL Decide→Act gate reads it); agents.phase is only a mirror and
  // can lag if it ever drifts. Prefer the registration so this surface never
  // contradicts the gate (e.g. header "Current phase · Decide" while the gate
  // shows "Advanced to Act ✓"). Native Lets have no registration → fall back to
  // agents.phase. See docs/design/BDA_PROMOTION_AND_PROTECTIONS.md §6.
  const agentPhase = (registration?.registrationStatus || agent?.phase || 'build').toLowerCase();

  /* Deep-link return: a "Back to <agent> — Build phase" link elsewhere navigates
   * to /agents/<id>?tab=promote&phase=build. Honor a valid ?phase= as the initial
   * inner tab (so the operator lands exactly where they left), then consume it so
   * it doesn't pin the sub-nav on later visits. */
  const [searchParams, setSearchParams] = useSearchParams();
  const phaseParam = (searchParams.get('phase') || '').toLowerCase();
  const initialInner = ['build', 'decide', 'act'].includes(phaseParam) ? phaseParam : agentPhase;
  const [innerPhase, setInnerPhase] = useState(initialInner);
  useEffect(() => {
    if (phaseParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('phase');
      setSearchParams(next, { replace: true });
    }
    // run once on mount to consume the param (deps intentionally omitted)
  }, []);

  /* Follow the agent's phase forward when it advances (e.g. operator clicks
   * Advance to Decide) — but don't fight manual sub-nav clicks backward. */
  const phaseRef = React.useRef(agentPhase);
  useEffect(() => {
    if (agentPhase !== phaseRef.current) {
      phaseRef.current = agentPhase;
      setInnerPhase(agentPhase);
    }
  }, [agentPhase]);

  const innerTabs = useMemo(() => {
    const ORDER = ['build', 'decide', 'act'];
    const curIdx = ORDER.indexOf(agentPhase);
    const dot = (id) => {
      const i = ORDER.indexOf(id);
      if (i < 0 || curIdx < 0) return undefined;
      if (i < curIdx) return 'success';
      if (i === curIdx) return 'action';
      return undefined;
    };
    const mark = (id) => (id === agentPhase ? ' · current' : '');
    return [
      { id: 'build',  label: 'Build' + mark('build'),   dot: dot('build') },
      { id: 'decide', label: 'Decide' + mark('decide'), dot: dot('decide') },
      { id: 'act',    label: 'Act' + mark('act'),       dot: dot('act') },
    ];
  }, [agentPhase]);

  const phaseLabel = { build: 'Build', decide: 'Decide', act: 'Act' }[agentPhase] || agentPhase;
  // True when the operator is viewing a phase that is NOT the agent's live phase
  // (e.g. clicked back to Decide while the agent is in Act) — surfaced so the
  // sub-surface's stale-looking gates read as "history", not "do this now".
  const viewingOffCurrent = innerPhase !== agentPhase;

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-bold tracking-tight m-0 text-v2-ink">Promote</h3>
          <Pill tone="cyan" size="sm">Current phase · {phaseLabel}</Pill>
        </div>
        <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
          Build · Decide · Act promotion gate. Identity, Intent, and access are set in their own steps;
          this gate verifies them and seals the promotion.
        </p>
      </div>

      <TabNav
        tabs={innerTabs}
        activeId={innerPhase}
        onChange={setInnerPhase}
        ariaLabel="Promotion phases"
      />

      {viewingOffCurrent && (
        <div className="rounded-v2-md border border-v2-border-subtle bg-v2-muted px-3 py-2 text-[12px] text-v2-ink-muted">
          You're viewing the <strong className="text-v2-ink">{{ build: 'Build', decide: 'Decide', act: 'Act' }[innerPhase] || innerPhase}</strong> phase,
          but this agent is currently in <strong className="text-v2-ink">{phaseLabel}</strong>. Gates and actions
          for an already-passed phase are shown for reference.
        </div>
      )}

      {innerPhase === 'build' && (
        <ExteletBuildSurface
          agent={agent}
          registration={registration}
          reloadRegistration={reloadRegistration}
          reloadAgentFromParent={reloadAgent}
          discoveryRunning={discoveryRunning}
          discoveredSkills={discoveredSkills}
          setDiscoveredSkills={setDiscoveredSkills}
          handleDiscoverInline={handleDiscoverInline}
          onAdvancePhase={onAdvancePhase}
          agentPhase={agent?.phase}
        />
      )}

      {innerPhase === 'decide' && (
        <ExteletDecideTab
          agent={agent}
          registration={registration}
          reloadRegistration={reloadRegistration}
          reloadAgentFromParent={reloadAgent}
        />
      )}

      {innerPhase === 'act' && (
        <ExteletActTab
          agent={agent}
          registration={registration}
          reloadRegistration={reloadRegistration}
          reloadAgentFromParent={reloadAgent}
        />
      )}
    </div>
  );
}
